import { z } from 'zod'

import { auditShape } from '@/shared/details'

export * from './paths'

const levelSchema = z.enum(['organization', 'service'])
const statusSchema = z.enum(['active', 'disabled', 'deleting'])

/** Whose requests a client makes: an organization's, or one of Skillable's own services'. */
export type ApiClientLevel = z.infer<typeof levelSchema>

/** Where a client stands; a deleted one is never shown. */
export type ApiClientStatus = z.infer<typeof statusSchema>

const apiClientSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    organizationId: z.string(),
    organizationName: z.string(),
    level: levelSchema,
    status: statusSchema,
    clientId: z.string(),
    tokenUrl: z.string().nullish(),
    scope: z.string().nullish(),
  })
  .extend(auditShape)

/** An API client as its details page and edit form show it. */
export type ApiClient = z.infer<typeof apiClientSchema>

export function parseApiClient(body: unknown): ApiClient {
  return apiClientSchema.parse(body)
}

const summarySchema = z.object({
  id: z.string(),
  name: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  level: levelSchema,
  status: statusSchema,
})

/** An API client as a list shows it. */
export type ApiClientSummary = z.infer<typeof summarySchema>

export function parseApiClientSummary(item: unknown): ApiClientSummary {
  return summarySchema.parse(item)
}

const nameSchema = z.object({ id: z.string(), name: z.string(), isDeleted: z.boolean() })

/** An API client's name, for "Created by" lines; a deleted one has no page. */
export function parseApiClientName(item: unknown): { readonly id: string; readonly name: string; readonly isDeleted: boolean } {
  return nameSchema.parse(item)
}

/** The API's code when a client's organization is deactivated, refusing a create or an enable. */
export const organizationInactiveCode = 'organization_profile.not_active'

const organizationSchema = z.object({ id: z.string(), name: z.string() })

/** The organization Create API Client is opened for. */
export function parseOrganization(body: unknown): { readonly id: string; readonly name: string } {
  return organizationSchema.parse(body)
}

const credentialKindSchema = z.enum(['secret', 'certificate'])

/** A secret or a certificate. */
export type CredentialKind = z.infer<typeof credentialKindSchema>

/** The kinds, in the order the Credentials tab offers them. */
export const credentialKinds: readonly CredentialKind[] = credentialKindSchema.options

const credentialSchema = z.object({
  id: z.string(),
  kind: credentialKindSchema,
  status: z.enum(['pending', 'active']),
  hint: z.string().nullish(),
  createdOn: z.string(),
  expiresOn: z.string(),
})

/** A client's secret or certificate: its hint or thumbprint and dates, never its value. */
export type ApiClientCredential = z.infer<typeof credentialSchema>

export function parseCredential(item: unknown): ApiClientCredential {
  return credentialSchema.parse(item)
}

const credentialListSchema = z.object({ data: z.array(z.unknown()) })

/** The client's credentials, from the list's `data`. */
export function parseCredentials(body: unknown): readonly ApiClientCredential[] {
  return credentialListSchema.parse(body).data.map(parseCredential)
}

const newSecretSchema = z.object({ id: z.string(), value: z.string() })

/** A new secret's value, which the API shows only in the answer that adds it. */
export function parseNewSecret(body: unknown): string {
  return newSecretSchema.parse(body).value
}

/** The most secrets, and the most certificates, a client holds. */
export const mostCredentialsOfEachKind = 2

/** How long a new secret may live, in months; 12 by default. */
export const secretLifetimes: readonly number[] = [3, 6, 12, 18, 24]
export const defaultSecretLifetime = 12
export const mostSecretLifetime = 24

/** How close an expiry is before the tab warns of it. */
export const expiryWarningDays = 30

/** The API's codes when a credential is refused. */
export const credentialLimitCode = 'api_client.credential_limit'
export const certificateExistsCode = 'api_client.certificate_exists'

/** Access Control's code when Super User is given to an API client. */
export const superUserRefusedCode = 'access_control.super_user_refused'
